import { Card, CardHeader } from "@/components/ui/card";
import { Switch } from "@/components/ui/misc";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useSetDomainTranscription } from "@/lib/queries";
import type { Domain } from "@/lib/types";

/**
 * Whether each domain's call recordings are turned into text (`transcribe_recordings`).
 *
 * Beside outbound routing because it is the same kind of decision: the firm's, about what happens
 * to its calls, and nothing to do with the switch topology the rest of the page shows. It is not a
 * rep's: whether a phone records at all is (`record_calls`), and this only decides what becomes of
 * the audio once there is some. So no recording, no transcript, whatever this says.
 *
 * A card with a row per domain rather than a column in the table above: the switch needs its one
 * line of explanation beside it, and a table cell repeating it per realm would be noise. Each
 * switch saves on its own, like a campaign's auto-answer — there is nothing else to fill in first.
 * Turning it off keeps the transcripts already written, and the help text says so, because "off"
 * on a setting about stored content reads as "delete" to anyone who has had to answer for it.
 */
export function DomainTranscriptionCard({ domains }: { domains: Domain[] }) {
  if (domains.length === 0) return null;
  return (
    <Card className="mt-4">
      <CardHeader
        title="Transcribe call recordings"
        description="Recordings made while it is on are turned into text automatically. Turning it off stops new ones and keeps existing transcripts."
      />
      <ul className="divide-y divide-border">
        {domains.map((d) => (
          <DomainTranscriptionRow key={d.id} domain={d} />
        ))}
      </ul>
    </Card>
  );
}

function DomainTranscriptionRow({ domain }: { domain: Domain }) {
  const toast = useToast();
  const save = useSetDomainTranscription();
  const labelId = `domain-transcription-${domain.id}`;

  const change = (next: boolean) =>
    save.mutate(
      { id: domain.id, transcribe_recordings: next },
      {
        onSuccess: () =>
          toast.success(
            next ? "Recordings will be transcribed" : "New recordings will not be transcribed",
            domain.name,
          ),
        onError: (e) =>
          toast.error(
            "Could not change transcription",
            e instanceof ApiError ? e.message : "Please try again.",
          ),
      },
    );

  return (
    <li className="flex items-center justify-between gap-4 px-4 py-2.5">
      <span id={labelId} className="font-mono text-xs text-text">
        <span className="sr-only">Transcribe call recordings in </span>
        {domain.name}
      </span>
      <Switch
        checked={domain.transcribe_recordings}
        onCheckedChange={change}
        disabled={save.isPending}
        aria-labelledby={labelId}
      />
    </li>
  );
}
